/**
 * IndexedDB Utility for caching branches
 * Stores branches with branchCode as key and timestamp for cache expiry
 */

const DB_NAME = 'CafeMenuDB';
const STORE_NAME = 'branches';
const CACHE_EXPIRY_MS = 3 * 60 * 60 * 1000; // 3 hours in milliseconds

/**
 * Initialize IndexedDB database
 */
function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'branchCode' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Store branches in IndexedDB
 * @param {Array} branches - Array of branch objects
 * @returns {Promise<void>}
 */
export async function cacheBranches(branches) {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    // Clear existing cache
    store.clear();

    // Add branches with timestamp
    const timestamp = Date.now();
    branches.forEach(branch => {
      store.add({
        ...branch,
        timestamp,
      });
    });

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error caching branches:', error);
    throw error;
  }
}

/**
 * Get all branches from IndexedDB
 * @returns {Promise<Array>} Array of branches
 */
export async function getCachedBranches() {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const branches = request.result;
        if (branches.length === 0) {
          resolve(null);
          return;
        }

        const hasOrderingConfig = branches.every((branch) => (
          Object.prototype.hasOwnProperty.call(branch, 'orderingEnabled')
        ));
        if (!hasOrderingConfig) {
          resolve(null);
          return;
        }

        // Check if cache is still valid
        const timestamp = branches[0].timestamp;
        const isExpired = Date.now() - timestamp > CACHE_EXPIRY_MS;

        if (isExpired) {
          resolve(null); // Cache expired
        } else {
          resolve(branches);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error retrieving cached branches:', error);
    return null;
  }
}

/**
 * Get a single branch by branchCode
 * @param {string} branchCode - Branch code to look up
 * @returns {Promise<Object|null>} Branch object or null
 */
export async function getBranchByCode(branchCode) {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(branchCode);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const branch = request.result;
        if (!branch) {
          resolve(null);
          return;
        }

        if (!Object.prototype.hasOwnProperty.call(branch, 'orderingEnabled')) {
          resolve(null);
          return;
        }

        // Check if cache is still valid
        const isExpired = Date.now() - branch.timestamp > CACHE_EXPIRY_MS;
        if (isExpired) {
          resolve(null);
        } else {
          resolve(branch);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error retrieving branch:', error);
    return null;
  }
}

/**
 * Clear all branches cache
 * @returns {Promise<void>}
 */
export async function clearBranchesCache() {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.clear();

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error clearing cache:', error);
    throw error;
  }
}
